import constants from '../../../../appConstants';
import { MultiSelectFilter, StaticFilter, DateTimeFilter } from '@dataGrid/components/columnFilters';
import { GeoCodeRenderer, DotRenderer ,  SpecialNeedsValueGetter,
  SpecialNeedsRenderer,} from '@dataGrid/components/cellRenderer';
import iconRenderer from '../../../common/iconRenderer';
import { formatDateTime, mergeUserConfig, suppressEnter } from '@dataGrid/gridUtilities';
import { DropdownEditor } from '@dataGrid/components/cellEditors';
import {
  holidayRangeValueFormatter,
  holidayRangeValueGetter
} from '../EditCustomer/components/Holidays/utils';
import StyledToolTip from '../../../common/StyledToolTip';
import { formatDayWindow, getDefaultHours, renderWindow } from '../EditCustomer/utils';
import { getSpecialNeedsData } from '../utils';

class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = 'GridConfiguration';
    this.config = GridConfig.getConfiguration(config);
  }
  static getConfiguration({featureFlags,userConfig}) {
    const defaultConfig =  GridConfig.getDefaultConfiguration({featureFlags});
    return mergeUserConfig({
      defaultConfig,
      userConfig,
    });
  }

  static formatIsGeoFenced = (params) => {
    return params.value ? 'Yes' : 'No';
  };

  static getPrefferedRoute = (params) => {
    return params.data.deliveryDetails?.route || '';
  };

  static getLiftGate = (params) => {
    return params.data.deliveryDetails.isLiftGateRequired ? 'L' : '';
  };

  static formatIsNotFME = (params) => {
    return params.data.deliveryDetails.isNoFME ? 'Y' : '';
  };

  static formatRequiresAppointment = (params) => {
    return params.data.deliveryDetails.isApt ? 'Y' : '';
  };

  static formatIgnoreAppointment=(params)=>{
    return params.data.deliveryDetails.isIgnoreApt ? 'Y' : '';
  }

  static formatTrailerBackFill=(params)=>{
    return params.data.deliveryDetails.isTrailerBackFill ? 'Y' : '';
  }

  static getIsNoCargoVanValue = (params) => {
    return params.data.deliveryDetails.isNoCargoVan ? 'Y' : '';
  };

  static holidayTooltipRenderer = (holidays) => {
    return holidays.map((date, index) => (
      <span key={index}>
        {date}
        <br />
      </span>
    ));
  };

  static holidayRenderer = (params) => {
    if (!params.value) {
      return null;
    }
    const holidayRangeValue = holidayRangeValueFormatter(params);
    return (
      <StyledToolTip title={GridConfig.holidayTooltipRenderer(holidayRangeValue)}>
        <div className="holiday-dates">{holidayRangeValue.join(', ')}</div>
      </StyledToolTip>
    );
  };

  static defaultWindowRenderer(params) {
    if (!params.value) {
      return null;
    }
    const tooltipMessage = params.value.map((eachDeliveryWindow, index) => (
      <span key={index}>
        {eachDeliveryWindow}
        <br />
      </span>
    ));
    return (
      <StyledToolTip title={tooltipMessage}>
        <div>{params.value.join(', ')}</div>
      </StyledToolTip>
    );
  }
  static getCustomerAddress(params) {
    return [params.data.address, params.data.address2].filter(Boolean).join(', ');
  }

  static defaultHoursValueGetter = (params) => {
    const {
      openHours = '',
      closeHours = '',
      openHours2 = '',
      closeHours2 = ''
    } = params.data['deliveryDetails'] ?? {};
    return getDefaultHours({ openHours, closeHours, openHours2, closeHours2 });
  };

  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static specialNeedsValueGetter(params, featureFlags) {
    const specialNeedsData = getSpecialNeedsData({
      data: params.data?.deliveryDetails,
      isDelivery: true,
      featureFlags
    });
    return SpecialNeedsValueGetter({
      data: specialNeedsData
    });
  }

  static specialNeedsRenderer(params) {
    return <SpecialNeedsRenderer {...{
      ...params,
      data: {
        ...params.data,
        ...params.data?.deliveryDetails || {}
      }
    }} />;
  }

  static getCustomerNameClasses(params) {
    return [
      'gridCell-leftAlign',
      params.value ? 'actionable-cell' : ''
    ];
  }

  static getDefaultConfiguration({featureFlags}) {
    return {
      title: constants.gridNames.CustomerGrid,
      keyColumn: 'code',
      rowSelection: 'multiple',
      rowDeselection: 'true',
      defaultColDef: {
        lockPinned: true,
        cellDataType:false
      },
      columns: [
        {
          headerName: '',
          minWidth: constants.columnWidth.VIOLATION_DOT,
          maxWidth: constants.columnWidth.VIOLATION_DOT,
          field: 'violation',
          pinned: 'left',
          lockPosition: true,
          lockPinned: true,
          cellClass: ['gridCell-centerAlign', 'gridCell-dot'],
          filter: false,
          cellRenderer: DotRenderer,
          isInvisibleInShowHide: true,
          gridTitle: constants.gridNames.CustomerGrid
        },
        {
          headerName: 'Acc #',
          headerTooltip: 'Customer Account Number',
          field: 'code',
          minWidth: constants.columnWidth.CUSTOMER_CODE,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipField: 'code',
          lockPosition: true
        },
        {
          headerName: 'Customer Name',
          headerTooltip: 'Customer Name',
          field: 'name',
          minWidth: constants.columnWidth.CUSTOMER,
          lockPosition: true,
          cellClass: GridConfig.getCustomerNameClasses,
          filter: MultiSelectFilter,
          tooltipField: 'name',
          filterParams: {
            width: constants.columnWidth.W_300
          }
        },
        {
          headerName: "Spl Needs",
          headerTooltip: "Special Needs",
          minWidth: constants.columnWidth.SPL_NEED,
          cellClass: "gridCell-leftAlign",
          valueGetter: (params) => GridConfig.specialNeedsValueGetter(params, featureFlags),
          filter: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption,
          },
          cellRenderer: GridConfig.specialNeedsRenderer,
        },
        {
          headerName: "Address",
          headerTooltip: "Customer Address",
          field: "address",
          minWidth: constants.columnWidth.CUSTOMER_ADDRESS,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          valueGetter: GridConfig.getCustomerAddress,
          tooltipValueGetter: (params) => params.value,
          filterParams: {
            width: constants.columnWidth.W_300
          }
        },
        {
          headerName: 'City',
          headerTooltip: 'City',
          field: 'city',
          cellClass: 'gridCell-leftAlign',
          minWidth: constants.columnWidth.CITY,
          filter: MultiSelectFilter,
          tooltipField: 'city'
        },
        {
          headerName: 'State',
          headerTooltip: 'State',
          field: 'state',
          cellClass: 'gridCell-leftAlign',
          minWidth: constants.columnWidth.STATE,
          filter: MultiSelectFilter
        },
        {
          headerName: 'Zip',
          headerTooltip: 'Zip',
          field: 'zip',
          cellClass: 'gridCell-leftAlign',
          minWidth: constants.columnWidth.ZIP_CODE,
          filter: MultiSelectFilter
        },
        {
          headerName: 'Geocoding',
          headerTooltip: 'Geocoding',
          field: 'deliveryDetails.isGeoCoded',
          minWidth: constants.columnWidth.GEOCODING,
          cellClass: 'gridCell-left,Align',
          editable: false,
          cellRenderer: GeoCodeRenderer,
          filter: StaticFilter,
          filterParams: {
            staticOptions: Object.values(constants.geoCode),
            renderIcon: iconRenderer
          }
        },
        {
          headerName: 'GeoFencing',
          headerTooltip: 'GeoFencing',
          field: 'isGeoFenced',
          valueFormatter: GridConfig.formatIsGeoFenced,
          minWidth: constants.columnWidth.GEOCODING,
          cellClass: 'gridCell-left,Align',
          editable: false,
          filter: StaticFilter,
          filterParams: {
            staticOptions: Object.values(constants.geoFenced),
            renderIcon: iconRenderer
          }
        },
        {
          headerName: 'Route',
          headerTooltip: 'Route',
          valueGetter: GridConfig.getPrefferedRoute,
          minWidth: constants.columnWidth.ROUTES,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Min Equip Size',
          headerTooltip: 'Min Equipment Size',
          field: 'deliveryDetails.minEquipmentSize',
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Max Equip Size',
          headerTooltip: 'Max Equipment Size',
          field: 'deliveryDetails.maxEquipmentSize',
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        ...(featureFlags.enableFixedEquipmentSize ? [
          {
            headerName: 'Fixed Equip Size',
            headerTooltip: 'Fixed Equipment Size',
            field: 'deliveryDetails.fixedEquipmentSize',
            minWidth: constants.columnWidth.FIXED_TRAILER,
            cellClass: 'gridCell-leftAlign',
            filter: MultiSelectFilter,
          }
        ] : []),
        {
          headerName: 'Liftgate',
          headerTooltip: 'Liftgate',
          valueGetter: GridConfig.getLiftGate,
          minWidth: constants.columnWidth.ROUTES,
          cellClass: 'gridCell-centerAlign',
          filter: MultiSelectFilter
        },
        ...(featureFlags.enableTrailerBackFillCustomer ? [
          {
            headerName: 'Trailer Drop',
            headerTooltip: 'Trailer Drop',
            field: 'deliveryDetails.isTrailerBackFill',
            valueFormatter: GridConfig.formatTrailerBackFill,
            minWidth: constants.columnWidth.W_130,
            cellClass: 'gridCell-leftAlign',
            filter: StaticFilter,
            filterParams: {
              staticOptions: Object.values(constants.BooleanFilter)
            }
          }
        ] : []),
        {
          headerName: 'Not FME',
          headerTooltip: 'Not FME',
          valueGetter: GridConfig.formatIsNotFME,
          minWidth: constants.columnWidth.W_110,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          hide: !featureFlags.enableFMECustomer,
          isInvisibleInShowHide: !featureFlags.enableFMECustomer,
          shouldForceHideColumn: !featureFlags.enableFMECustomer,
        },
        {
          headerName: 'No Cargo Van',
          headerTooltip: 'No Cargo Van',
          valueGetter: GridConfig.getIsNoCargoVanValue,
          minWidth: constants.columnWidth.W_150,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          hide: !featureFlags.enableNoCargoVanCustomer,
          isInvisibleInShowHide: !featureFlags.enableNoCargoVanCustomer,
          shouldForceHideColumn: !featureFlags.enableNoCargoVanCustomer,
        },
        {
          headerName: 'Default Hours',
          headerTooltip: 'Default Hours',
          valueGetter: GridConfig.defaultHoursValueGetter,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Monday',
          headerTooltip: 'Monday',
          field: 'deliveryDetails.receivingHours.monday',
          tooltipField: 'deliveryDetails.receivingHours.monday',
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          cellRenderer: renderWindow,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Tuesday',
          headerTooltip: 'Tuesday',
          field: 'deliveryDetails.receivingHours.tuesday',
          tooltipField: 'deliveryDetails.receivingHours.tuesday',
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          cellRenderer: renderWindow,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Wednesday',
          headerTooltip: 'Wednesday',
          field: 'deliveryDetails.receivingHours.wednesday',
          tooltipField: 'deliveryDetails.receivingHours.wednesday',
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          cellRenderer: renderWindow,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Thursday',
          headerTooltip: 'Thursday',
          field: 'deliveryDetails.receivingHours.thursday',
          tooltipField: 'deliveryDetails.receivingHours.thursday',
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          cellRenderer: renderWindow,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Friday',
          headerTooltip: 'Friday',
          field: 'deliveryDetails.receivingHours.friday',
          tooltipField: 'deliveryDetails.receivingHours.friday',
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          cellRenderer: renderWindow,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Saturday',
          headerTooltip: 'Saturday',
          field: 'deliveryDetails.receivingHours.saturday',
          tooltipField: 'deliveryDetails.receivingHours.saturday',
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          cellRenderer: renderWindow,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Sunday',
          headerTooltip: 'Sunday',
          field: 'deliveryDetails.receivingHours.sunday',
          tooltipField: 'deliveryDetails.receivingHours.sunday',
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          cellRenderer: renderWindow,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Holiday',
          headerTooltip: 'Holiday',
          field: 'holidays',
          cellClass: 'gridCell-leftAlign',
          filter: DateTimeFilter,
          minWidth: constants.columnWidth.W_170,
          cellEditor: DropdownEditor,
          cellEditorPopup: true,
          editable: false,
          bulkEditable: true,
          cellEditorParams: {
            editorFieldName: 'holidayOptions',
            useGetterValue: true
          },
          filterParams: {
            DATETYPE: constants.DATETYPES.MONTHDATEYEAR,
            useGetterValue: true
          },
          suppressKeyboardEvent: suppressEnter,
          valueGetter: holidayRangeValueGetter,
          cellRenderer: GridConfig.holidayRenderer
        },
        {
          headerName: 'Stop Time',
          headerTooltip: 'Stop Time',
          field: 'deliveryDetails.stopTime',
          minWidth: constants.columnWidth.W_110,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        ...(featureFlags.enableRequiresAppointmentInCustomerManagement
          ? [
              {
                headerName: 'Req APT',
                headerTooltip: 'Requires Appointment',
                field: 'deliveryDetails.isApt',
                valueFormatter: GridConfig.formatRequiresAppointment,
                minWidth: constants.columnWidth.W_100,
                cellClass: 'gridCell-leftAlign',
                filter: StaticFilter,
                filterParams: {
                  staticOptions: Object.values(constants.BooleanFilter)
                }
              },
              {
                headerName: 'Min HU',
                headerTooltip: 'Min HU',
                field: 'deliveryDetails.minHuForApt',
                minWidth: constants.columnWidth.W_100,
                cellClass: 'gridCell-leftAlign',
                filter: MultiSelectFilter
              }
            ]
          : []),
        ...(featureFlags.enableIgnoreAppointmentInCustomerManagement
          ? [
              {
                headerName: 'Ign APT',
                headerTooltip: 'Ignore appointment',
                field: 'deliveryDetails.isIgnoreApt',
                valueFormatter: GridConfig.formatIgnoreAppointment,
                minWidth: constants.columnWidth.W_100,
                cellClass: 'gridCell-leftAlign',
                filter: StaticFilter,
                filterParams: {
                  staticOptions: Object.values(constants.BooleanFilter)
                }
              }
            ]
          : []),
        {
          headerName: 'Last Modified On',
          headerTooltip: 'Last Modified On',
          field: 'deliveryDetails.lastModified.dateTime',
          valueFormatter: formatDateTime,
          minWidth: constants.columnWidth.W_120,
          cellClass: 'gridCell-leftAlign',
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME,
          }
        },
        {
          headerName: 'Last Modified By',
          headerTooltip: 'Last Modified By',
          field: 'deliveryDetails.lastModified.userName',
          minWidth: constants.columnWidth.W_180,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipValueGetter: (params) => params.value
        }
      ]
    };
  }
}

export default GridConfig;
